/**
 * Cache module-level + helper di arricchimento per WhatsApp chat store.
 * Le cache stanno fuori dallo state Zustand per evitare re-render inutili.
 */

import type { ChatOverview } from '../../types/whatsappChat';
import { isMaskedPhoneName } from '../../services/whatsapp/chatListRules';

// ============ GROUP NAMES CACHE ============
let _groupNamesCache: Map<string, string> = new Map();
let _groupNamesCacheTimestamp = 0;
export const GROUP_NAMES_CACHE_TTL = 5 * 60 * 1000; // 5 minuti

export function getGroupNamesCache(): Map<string, string> {
  return _groupNamesCache;
}
export function setGroupNamesCache(map: Map<string, string>): void {
  _groupNamesCache = map;
}
export function getGroupNamesCacheTimestamp(): number {
  return _groupNamesCacheTimestamp;
}
export function setGroupNamesCacheTimestamp(ts: number): void {
  _groupNamesCacheTimestamp = ts;
}

// ============ CONTACTS CACHE TTL ============
let _contactsCacheTimestamp = 0;
export const CONTACTS_CACHE_TTL = 5 * 60 * 1000; // 5 minuti

export function getContactsCacheTimestamp(): number {
  return _contactsCacheTimestamp;
}
export function setContactsCacheTimestamp(ts: number): void {
  _contactsCacheTimestamp = ts;
}

// ============ LID RESOLUTION STATE ============
// Vivono qui per essere resettabili insieme alle altre cache.
// Usate da lidResolution.ts.
export const _lidResolutionState = {
  inProgress: false,
  attempted: new Set<string>(),
};

/**
 * Pulisce tutte le cache module-level. Chiamare durante il cambio sessione
 * perché reset() di Zustand resetta solo lo state, non le variabili module-level.
 */
export function clearWhatsAppChatCaches(): void {
  _groupNamesCache.clear();
  _groupNamesCacheTimestamp = 0;
  _contactsCacheTimestamp = 0;
  _lidResolutionState.attempted.clear();
  _lidResolutionState.inProgress = false;
}

// ============ ENRICHMENT HELPERS ============

/**
 * Arricchisce le chat con i nomi corretti dei gruppi dalla cache.
 * /chats/overview non restituisce i nomi dei gruppi in modo affidabile,
 * quindi usiamo /api/groups come fonte autoritativa.
 */
export function enrichGroupNames(chats: ChatOverview[]): ChatOverview[] {
  if (_groupNamesCache.size === 0) return chats;
  return chats.map(chat => {
    if (chat.id.endsWith('@g.us')) {
      const groupName = _groupNamesCache.get(chat.id);
      if (groupName && groupName !== chat.name) {
        return { ...chat, name: groupName };
      }
    }
    return chat;
  });
}

/**
 * Arricchisce chat @lid con nomi dalla contacts cache.
 * WAHA NOWEB può restituire chat @lid con solo l'ID numerico come nome.
 */
export function enrichLidNames(chats: ChatOverview[], contactsMap: Map<string, string>): ChatOverview[] {
  if (contactsMap.size === 0) return chats;
  return chats.map(chat => {
    if (!chat.id.endsWith('@lid')) return chat;
    const idNum = chat.id.split('@')[0];
    if (chat.name === idNum || chat.name === chat.id) {
      const name = contactsMap.get(chat.id) || contactsMap.get(idNum);
      if (name) return { ...chat, name };
    }
    return chat;
  });
}

/**
 * Re-enrich nomi sender per messaggi di gruppo.
 * transformChat gira dentro getChatsOverview, prima che la contacts cache sia pronta.
 * Questo step corregge i nomi sender dopo che la cache è disponibile.
 */
export function enrichSenderNames(chats: ChatOverview[], contactsMap: Map<string, string>): ChatOverview[] {
  if (contactsMap.size === 0) return chats;
  return chats.map(chat => {
    if (!chat.lastMessage?.senderJid || chat.lastMessage.senderName || chat.lastMessage.fromMe) return chat;
    const name = contactsMap.get(chat.lastMessage.senderJid) ||
      contactsMap.get(chat.lastMessage.senderJid.split('@')[0]);
    if (!name) return chat;
    return { ...chat, lastMessage: { ...chat.lastMessage, senderName: name } };
  });
}

/**
 * Arricchisce chat 1:1 @c.us con nomi dalla contacts cache.
 * WAHA NOWEB spesso restituisce solo il numero di telefono come nome.
 */
export function enrichChatNames(chats: ChatOverview[], contactsMap: Map<string, string>): ChatOverview[] {
  if (contactsMap.size === 0) return chats;
  return chats.map(chat => {
    if (!chat.id.endsWith('@c.us')) return chat;
    const phoneNumber = chat.id.split('@')[0];
    // Controlla se il nome sembra un numero di telefono (con o senza +, spazi, trattini)
    const nameNormalized = chat.name.replace(/[\s\-\(\)\+]/g, '');
    const looksLikePhone = nameNormalized === phoneNumber ||
      chat.name === chat.id ||
      /^\+?\d[\d\s\-()]{5,}$/.test(chat.name);
    if (looksLikePhone) {
      const name = contactsMap.get(chat.id) || contactsMap.get(phoneNumber);
      if (name && name.replace(/[\s\-\(\)\+]/g, '') !== phoneNumber && !isMaskedPhoneName(name)) {
        return { ...chat, name };
      }
    }
    return chat;
  });
}
